// O funil das 10 etapas da perícia judicial e as faixas do fluxo financeiro. Portado da Central do Perito
// (`src/lib/pipeline.ts`) em 03/11 (000385); mudaram só as CORES, que lá eram os tokens do shadcn e aqui são
// os do hub (ver `ETAPA_COR`).
import type { StatusPericia } from "./tipos";

/** O funil oficial do processo pericial judicial — 10 etapas fixas. */
export const ETAPAS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

export type EtapaNum = (typeof ETAPAS)[number];

export const TOTAL_ETAPAS = 10;

export const ETAPA_LABEL: Record<EtapaNum, string> = {
  1: "Nomeação",
  2: "Aceitação",
  3: "Honorários Arbitrados",
  4: "Depósito Judicial",
  5: "Documentação",
  6: "Análise",
  7: "Laudo",
  8: "Manifestações",
  9: "Liberação",
  10: "Levantamento",
};

/**
 * A cor de cada etapa (o ponto do filtro, a faixa da coluna do kanban), por FASE do processo: cinza na
 * entrada (nomeação e aceite), a família de alerta no dinheiro que ainda não chegou (honorários arbitrados e
 * depósito), o azul de interface no trabalho do perito (documentação a manifestações), o laranja de ícone na
 * liberação e o verde no levantamento. Token semântico, porque o ponto precisa aparecer nos dois temas; o
 * laranja é `laranja-texto`, e não `laranja`: o da marca dá 2,13 : 1 no fundo claro, abaixo do 3 : 1 de
 * forma não textual.
 */
export const ETAPA_COR: Record<EtapaNum, string> = {
  1: "bg-cinza/40",
  2: "bg-cinza/70",
  3: "bg-alerta-tinta/70",
  4: "bg-alerta-tinta",
  5: "bg-acento/45",
  6: "bg-acento/65",
  7: "bg-acento/85",
  8: "bg-acento",
  9: "bg-laranja-texto",
  10: "bg-sucesso",
};

/** As chaves do kanban antigo de lá → a etapa numérica. */
const LEGADO_MAP: Record<string, EtapaNum> = {
  nomeacao: 1,
  aceite: 2,
  proposta: 3,
  deposito: 4,
  documentos: 5,
  diligencias: 6,
  laudo_elaboracao: 7,
  laudo_protocolado: 7,
  esclarecimentos: 8,
  encerrada: 10,
};

export function etapaValida(n: unknown): EtapaNum | null {
  const v = Number(n);
  return Number.isInteger(v) && v >= 1 && v <= 10 ? (v as EtapaNum) : null;
}

/** A etapa atual de uma perícia, aceitando o dado legado. */
export function etapaNumDe(p: { etapa_atual?: number; etapa?: string; status?: StatusPericia }): EtapaNum {
  const direta = etapaValida(p.etapa_atual);
  if (direta) return direta;
  if (p.etapa && LEGADO_MAP[p.etapa]) return LEGADO_MAP[p.etapa];
  return p.status === "finalizado" || p.status === "arquivado" ? 10 : 1;
}

export function etapaAtiva(p: { etapa_atual?: number; etapa?: string; status?: StatusPericia }) {
  return etapaNumDe(p) < 10;
}

/**
 * O STATUS da perícia, DERIVADO da etapa: a 10 (Levantamento) é a finalizada. Lá a coluna era guardada e só a
 * edição a gravava como "finalizado" — mover para a etapa 10 pelo kanban deixava a perícia "em andamento". O
 * banco devolve o mesmo valor em `perito_ler`; esta função existe para a tela, que muda a etapa na memória
 * antes de o banco responder.
 */
export function statusDaEtapa(etapa: EtapaNum): StatusPericia {
  return etapa === 10 ? "finalizado" : "andamento";
}

/** As faixas do fluxo financeiro judicial. */
export const FAIXAS_FLUXO = [
  { id: "arbitramento", label: "Aguardando Arbitramento", etapas: [1, 2], cor: "bg-cinza/50", texto: "text-cinza" },
  { id: "deposito", label: "Aguardando Depósito", etapas: [3], cor: "bg-alerta-tinta", texto: "text-alerta-tinta" },
  { id: "depositado", label: "Depositado", etapas: [4, 5, 6, 7, 8], cor: "bg-acento", texto: "text-acento" },
  { id: "liberado", label: "Liberado p/ Levantar", etapas: [9], cor: "bg-laranja-texto", texto: "text-alerta-tinta" },
  { id: "levantado", label: "Levantado", etapas: [10], cor: "bg-sucesso", texto: "text-aviso-tinta" },
] as const;

export type FaixaFluxo = (typeof FAIXAS_FLUXO)[number]["id"];
